import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { iaRa } from 'lib/helpers/utility';

import useClinicalNoteState from 'components/practice/charts/ClinicalNote/CurrentNote/TypeNotes/hooks/useClinicalNoteState';
import NarrativeForm from 'components/practice/charts/ClinicalNote/shared/NarrativeForm';

import { useClinicalNoteContext } from '../../../../../lib/context/ClinicalNoteContext/ClinicalNoteContext';
import { permissions } from '../../../../state';
import { ReviewSystems as initialValue } from '../CustomFormInitialStates';
import Header from '../components/Header';
import { scrollIntoNarrativeView } from '../lib/customFormsHelper';
import { createReviewOfSystemsNarrative } from '../lib/reviewSystemsHelper';
import useNewDesign from '../lib/useNewDesign';

import RSPatientBlock from './RSPatientBlock';
import RSPractitionerBlock from './RSPractitionerBlock';

import './ReviewSystems.scss';

const ReviewSystems = ({
  forwardedRef,
  defaultValue,
  isClinicalNote,
  showNarrative = false,
  sectionRef,
  onChange,
  options = [],
  customCN,
  setCustomCN
}) => {
  const [reviewSystems, setReviewSystems] = useState({
    practitioner: [...initialValue],
    patient: [...initialValue],
    noDetails: false,
    narrative: ''
  });
  const [clinicalNote, setClinicalNote] = useClinicalNoteState({ customCN, setCustomCN });
  const { cnDisplaySettings, noteInactiveState } = useClinicalNoteContext() || {};
  const { practice_id } = useRecoilValue(permissions);
  const role = practice_id ? 'practitioner' : 'patient';

  const [syncNarrative, setSyncNarrative] = useState(true);
  const advancedForm = cnDisplaySettings
    ? cnDisplaySettings?.sections?.hp?.ros?.advancedForm
    : true;

  const textAreaRef = useRef();

  useEffect(() => {
    if (defaultValue) {
      if (typeof defaultValue === 'string') {
        setReviewSystems(JSON.parse(defaultValue));
      } else {
        setReviewSystems(defaultValue);
      }
    } else {
      setReviewSystems({
        practitioner: [...initialValue],
        patient: [...initialValue]
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    notifyChange(reviewSystems);
  }, [reviewSystems]);

  const notifyChange = (updatedValue) => {
    if (onChange) {
      onChange({ target: { value: updatedValue, name: 'ReviewSystems', type: 'custom' } });
    }
  };

  useImperativeHandle(sectionRef, () => ({
    formData: { ros: reviewSystems }
  }));

  const handleOnChange = (key, value, blockIdx, rowIdx, isPatient) => {
    const updatedObject = {
      ...reviewSystems,
      [role]: reviewSystems[role].map((block, idx) => {
        if (idx === blockIdx && rowIdx === false) {
          return {
            ...block,
            noBlock: isPatient ? value : !reviewSystems[role][idx].noBlock
          };
        } else if (idx === blockIdx && rowIdx >= 0) {
          return {
            ...block,
            noBlock: false,
            row: block.row.map((row, idx) => {
              if (idx === rowIdx) {
                return { ...row, [key]: value };
              } else {
                return row;
              }
            })
          };
        } else {
          return block;
        }
      })
    };

    if (syncNarrative) {
      handleNarrative({
        rosObject: updatedObject,
        event: createReviewOfSystemsNarrative(updatedObject)
      });
    } else {
      setReviewSystems(updatedObject);
      saveClinicalNote(updatedObject);
    }
  };

  const handleNarrative = ({
    rosObject = reviewSystems,
    event,
    scrollable = false,
    sync = true
  }) => {
    if (typeof event === 'object' && event?.target?.value) {
      event = event?.target?.value;
    }

    setSyncNarrative(sync);

    const updatedObject = { ...rosObject, narrative: event };

    setReviewSystems(updatedObject);
    saveClinicalNote(updatedObject);

    scrollable && scrollIntoNarrativeView({ ref: textAreaRef });
  };

  const saveClinicalNote = (updatedObject) => {
    setClinicalNote &&
      setClinicalNote((prevState) => ({
        ...prevState,
        ros: updatedObject
      }));
  };

  const narrativeOptions = [
    {
      group: 'actions',
      icon: 'update-mono',
      title: 'Update & Sync',
      onClick: () =>
        handleNarrative({
          event: createReviewOfSystemsNarrative(reviewSystems),
          scrollable: true
        })
    }
  ];

  const newDesign = useNewDesign();

  return (
    <div>
      <input type="hidden" value={JSON.stringify(reviewSystems)} ref={forwardedRef} />
      <Header
        isAdvancedForm={advancedForm}
        title="Review Systems"
        noDetails={reviewSystems.noDetails}>
        <div className={cs('ReviewSystems', 'CustomForms', role === 'patient' && 'isPatient')}>
          {role === 'practitioner' && !newDesign && (
            <div className="ReviewSystems__body">
              <div className="ReviewSystems__patient">
                <p className="ReviewSystems__title">Patient response</p>
                {reviewSystems?.patient?.map((block, idx) => (
                  <RSPractitionerBlock locked={true} key={idx} blockIdx={idx} {...block} disabled />
                ))}
              </div>
              <div className="ReviewSystems__practitioner">
                <p className="ReviewSystems__title">Provider response</p>
                {reviewSystems?.practitioner?.map((block, idx) => (
                  <RSPractitionerBlock
                    key={idx}
                    blockIdx={idx}
                    onChange={handleOnChange}
                    {...block}
                    locked={isClinicalNote && (clinicalNote?.locked || noteInactiveState)}
                  />
                ))}
              </div>
            </div>
          )}
          {(role === 'patient' || newDesign) && (
            <div className="flex flex-col !gap-4">
              {reviewSystems?.patient?.map((block, idx) => (
                <RSPatientBlock
                  key={idx}
                  blockIdx={idx}
                  onChange={handleOnChange}
                  {...block}
                  locked={isClinicalNote && (clinicalNote?.locked || noteInactiveState)}
                />
              ))}
            </div>
          )}
        </div>
      </Header>
      {cnDisplaySettings && showNarrative && (
        <div className="!mt-3">
          <NarrativeForm
            syncNarrative={syncNarrative}
            setSyncNarrative={setSyncNarrative}
            onChange={(event) => handleNarrative({ event, sync: false })}
            setCurrentForm={setReviewSystems}
            restData={{
              className: 'w-full',
              label: 'Review Of Systems Narrative',
              placeholder: 'Add narrative here',
              id: 'Narrative-ros',
              'data-qa': 'narrative-ros',
              name: 'Narrative-ros',
              value: reviewSystems?.narrative,
              forwardedRef: textAreaRef,
              formName: 'ros',
              customFormId: ''
            }}
            narrativeOptions={[...narrativeOptions, ...iaRa(options)]}
          />
        </div>
      )}
    </div>
  );
};

export default ReviewSystems;
